import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { ContentBlock } from '../components/ContentBlock';
import { EditGameModal } from '../components/EditGameModal';
import { FavoriteButton } from '../components/FavoriteButton';
import { useAuth } from '../contexts/AuthContext';
import { useRegion } from '../contexts/RegionContext';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { Comments } from '../components/Comments';
import { HiDownload, HiSparkles, HiExclamation, HiPencilAlt } from 'react-icons/hi';

interface Game {
  id: string;
  slug: string;
  title: string;
  description: string;
  image_icon: string;
  download_url?: string;
  download_title?: string;
  status: 'in_progress' | 'released' | 'coming_soon';
  is_unreleased?: boolean;
  is_visible: boolean;
  content_blocks: Array<{
    type: 'image' | 'video' | 'text';
    src?: string;
    alt?: string;
    caption?: string;
    content?: string;
  }>;
}

export const GameDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, direction, language } = useRegion();
  const isAdmin = useIsAdmin();
  const [game, setGame] = useState<Game | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGame = async () => {
      if (!slug) return;

      try {
        const { data, error } = await supabase
          .from('games')
          .select('*')
          .eq('slug', slug)
          .single();

        if (error) throw error;
        if (!data || !data.is_visible) {
          navigate('/404');
          return;
        }

        setGame(data);
      } catch (error) {
        console.error('Error fetching game:', error);
        navigate('/404');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGame();
  }, [slug, navigate]);

  const handleUpdateGame = async (updatedGame: Game) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .update(updatedGame)
        .eq('id', game?.id)
        .select()
        .single();

      if (error) {
        // If download_title or is_unreleased column does not exist in DB yet
        if (error.code === '42703' || error.message?.includes('column')) {
          const { download_title, is_unreleased, ...safePayload } = updatedGame;
          const retryRes = await supabase
            .from('games')
            .update(safePayload)
            .eq('id', game?.id)
            .select()
            .single();

          if (retryRes.error) throw retryRes.error;
          setGame({ ...retryRes.data, download_title: updatedGame.download_title });
          return;
        }
        throw error;
      }
      setGame(data);
    } catch (error) {
      console.error('Error updating game:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">{t('games.loading')}</div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('games.notFound')}</div>
      </div>
    );
  }

  const isUnreleased = game.status === 'coming_soon' || Boolean(game.is_unreleased);
  const hasDownloadUrl = Boolean(game.download_url && game.download_url.trim().length > 0);
  const downloadButtonTitle = game.download_title || t('games.downloadGame');

  const canonicalUrl = `https://activelegend.ir/games/${game.slug}`;
  const gameJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'VideoGame',
        '@id': `${canonicalUrl}#game`,
        name: game.title,
        description: game.description,
        image: game.image_icon || 'https://activelegend.ir/AE-logo.png',
        url: canonicalUrl,
        inLanguage: language === 'fa' ? 'fa-IR' : 'en-US',
        applicationCategory: 'Game',
        operatingSystem: 'Windows 10, Windows 11',
        genre: ['Action', 'Competitive 1v1', 'Indie Game'],
        playMode: 'MultiPlayer',
        author: {
          '@type': 'Organization',
          name: 'Active Legend',
          url: 'https://activelegend.ir',
          logo: 'https://activelegend.ir/AE-logo.png',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Active Legend',
          url: 'https://activelegend.ir',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '45',
          bestRating: '5',
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: t('games.breadcrumbHome'),
            item: 'https://activelegend.ir/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: t('games.breadcrumbGames'),
            item: 'https://activelegend.ir/games',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: game.title,
            item: canonicalUrl,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-black pt-28 md:pt-36 pb-20" dir={direction}>
      <Helmet>
        <title>{`${game.title} | ${t('games.downloadGame')} | Active Legend`}</title>
        <meta
          name="description"
          content={game.description || `دانلود بازی ${game.title} ساخته‌شده توسط استودیو اکتیو لجند.`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${game.title} | Active Legend`} />
        <meta property="og:description" content={game.description || game.title} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={game.image_icon} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${game.title} | Active Legend`} />
        <meta name="twitter:description" content={game.description || game.title} />
        <meta name="twitter:image" content={game.image_icon} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(gameJsonLd) }} />
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Visual Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs text-gray-400">
          <Link to="/" className="hover:text-primary transition-colors">{t('games.breadcrumbHome')}</Link>
          <span>/</span>
          <Link to="/games" className="hover:text-primary transition-colors">{t('games.breadcrumbGames')}</Link>
          <span>/</span>
          <span className="text-gray-300 font-bold">{game.title}</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">{game.title}</h1>
          <p className="text-base font-medium text-gray-300 mb-6 leading-relaxed">{game.description}</p>
          
          {/* Availability State Handling */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            {isUnreleased ? (
              // State 2: Unreleased / Coming Soon
              <div className="flex-1 p-4 rounded-2xl bg-blue-950/40 border border-blue-500/30 flex items-start gap-3.5 text-blue-200">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 mt-0.5">
                  <HiSparkles className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm md:text-base font-bold text-white mb-1">
                    {t('games.comingSoonTitle')}
                  </h4>
                  <p className="text-xs md:text-sm text-blue-200/80 leading-relaxed">
                    {t('games.comingSoonDesc')}
                  </p>
                </div>
              </div>
            ) : hasDownloadUrl ? (
              // State 1: Available for Download
              <a
                href={game.download_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 bg-[#F4B744] hover:bg-[#F4B744]/90 text-black font-black px-7 py-3.5 rounded-2xl hover:scale-105 transition-all duration-200 shadow-xl shadow-[#F4B744]/20"
              >
                <HiDownload className="w-5 h-5" />
                <span>{downloadButtonTitle}</span>
              </a>
            ) : (
              // State 3: Unavailable (no download link registered)
              <div className="flex-1 p-4 rounded-2xl bg-zinc-900 border border-white/10 flex items-center gap-3 text-zinc-400">
                <HiExclamation className="w-5 h-5 text-amber-500 shrink-0" />
                <span className="text-xs md:text-sm">
                  {t('games.unavailableDesc')}
                </span>
              </div>
            )}

            <div className="flex items-center gap-3">
              {user && game && (
                <FavoriteButton gameId={game.id} />
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-2xl hover:scale-105 transition-all duration-200 text-sm"
                >
                  <HiPencilAlt className="w-4 h-4" />
                  <span>{t('games.editGame')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Blocks with Video & Image & Text */}
        <div className="space-y-8">
          {Array.isArray(game.content_blocks) && game.content_blocks.map((block, index) => (
            <ContentBlock key={index} block={block} index={index} />
          ))}
        </div>

        {isAdmin && (
          <EditGameModal
            game={game}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSave={handleUpdateGame}
          />
        )}

        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Comments gameId={game.id} />
          </div>
        </div>
      </div>
    </div>
  );
};
